import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useContext, useCallback, useState, } from "react";
import { AUTH_STATE_CHANGED_EVENT, AUTH_USER_UPDATED_EVENT, } from "@gsb-core/core";
import { loginInputSchema, sessionUserSchema, } from "./contracts.js";
import { AuthMfaRequiredError, } from "@gsb-core/auth-client";
export { AuthClientError, AuthMfaRequiredError, createCoreAuthClient, createCorePublicAuthClient, } from "@gsb-core/auth-client";
const AuthContext = createContext(null);
export function AuthProvider({ client, recoveryClient, children, }) {
    const [session, setSessionState] = useState(null);
    const [sessionNotice, setSessionNotice] = useState(() => client?.sessionEndReason?.() === "remote-signout"
        ? "You were signed out in another tab."
        : null);
    const setSession = useCallback((value) => {
        setSessionState(value);
        if (value)
            setSessionNotice(null);
    }, []);
    React.useEffect(() => {
        const clearSession = (event) => {
            setSessionState(null);
            setSessionNotice(event instanceof CustomEvent && event.detail === "remote-signout"
                ? "You were signed out in another tab."
                : null);
        };
        globalThis.addEventListener(AUTH_STATE_CHANGED_EVENT, clearSession);
        return () => globalThis.removeEventListener(AUTH_STATE_CHANGED_EVENT, clearSession);
    }, []);
    React.useEffect(() => {
        if (!client)
            return;
        // Profile edits change the cached user only; re-read it so the shell shows the saved name.
        const syncUser = () => {
            void client.session().then((next) => {
                if (next)
                    setSession(next);
            }, () => undefined);
        };
        globalThis.addEventListener(AUTH_USER_UPDATED_EVENT, syncUser);
        return () => globalThis.removeEventListener(AUTH_USER_UPDATED_EVENT, syncUser);
    }, [client, setSession]);
    return (_jsx(AuthContext.Provider, { value: {
            client,
            recoveryClient: recoveryClient ?? client,
            user: session?.user ?? null,
            csrfToken: session?.csrfToken ?? null,
            sessionNotice,
            setSession,
        }, children: children }));
}
function useAuthContext() {
    const context = useContext(AuthContext);
    if (!context)
        throw new Error("AuthProvider is required");
    return context;
}
export function useAuthClient() {
    const client = useAuthContext().client;
    if (!client)
        throw new Error("AuthClient is required");
    return client;
}
export function usePasswordRecoveryClient() {
    return useAuthContext().recoveryClient;
}
export function useLogin() {
    const context = useAuthContext();
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(false);
    const login = async (input) => {
        setPending(true);
        setError(null);
        try {
            if (!context.client) {
                throw new Error("Authentication is not configured");
            }
            const result = await context.client.login(loginInputSchema.parse(input));
            context.setSession({
                user: sessionUserSchema.parse(result.user),
                csrfToken: result.csrfToken,
            });
            return result;
        }
        catch (cause) {
            setError(cause instanceof AuthMfaRequiredError
                ? null
                : cause instanceof Error
                    ? cause.message
                    : "Login failed");
            throw cause;
        }
        finally {
            setPending(false);
        }
    };
    const adoptSession = async (auth) => {
        setPending(true);
        setError(null);
        try {
            if (!context.client?.adoptSession) {
                throw new Error("Server-issued session adoption is not configured");
            }
            const result = await context.client.adoptSession(auth);
            context.setSession({
                user: sessionUserSchema.parse(result.user),
                csrfToken: result.csrfToken,
            });
            return result;
        }
        catch (cause) {
            setError(cause instanceof Error ? cause.message : "Login failed");
            throw cause;
        }
        finally {
            setPending(false);
        }
    };
    return {
        error,
        pending,
        login,
        adoptSession,
        sessionNotice: context.sessionNotice,
    };
}
export function useGoogleLogin() {
    const context = useAuthContext();
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(false);
    const loginWithGoogle = async (credential) => {
        if (!context.client?.loginWithGoogle) {
            throw new Error("Google authentication is not configured");
        }
        setPending(true);
        setError(null);
        try {
            const result = await context.client.loginWithGoogle(credential);
            context.setSession({
                user: sessionUserSchema.parse(result.user),
                csrfToken: result.csrfToken,
            });
            return result;
        }
        catch (cause) {
            setError(cause instanceof Error ? cause.message : "Google authentication failed");
            throw cause;
        }
        finally {
            setPending(false);
        }
    };
    return { error, pending, loginWithGoogle };
}
function formText(data, name) {
    const value = data.get(name);
    return typeof value === "string" ? value : "";
}
function callbackUrl() {
    if (typeof globalThis.location === "undefined")
        return undefined;
    return (new URLSearchParams(globalThis.location.search).get("callbackUrl") ??
        undefined);
}
export function useSession() {
    const context = useAuthContext();
    const refresh = useCallback(async () => {
        if (!context.client) {
            context.setSession(null);
            return null;
        }
        const session = await context.client.session();
        context.setSession(session);
        return session;
    }, [context]);
    const logout = useCallback(async () => {
        if (!context.csrfToken || !context.client)
            throw new Error("Authentication is not configured");
        await context.client.logout(context.csrfToken);
        context.setSession(null);
    }, [context]);
    return {
        user: context.user,
        pending: false,
        refresh,
        logout,
    };
}
export function LoginForm({ onSuccess, className, footer }) {
    const { login, pending, error } = useLogin();
    const [mfaNotice, setMfaNotice] = useState(null);
    const submit = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const data = new FormData(form);
        setMfaNotice(null);
        try {
            const result = await login({
                email: formText(data, "email"),
                password: formText(data, "password"),
                remember: data.get("remember") === "on",
                tenantCode: formText(data, "tenantCode"),
                redirectTo: callbackUrl(),
            });
            onSuccess?.(result.redirectTo);
        }
        catch (cause) {
            if (cause instanceof AuthMfaRequiredError) {
                setMfaNotice("Email verification is required. This legacy sign-in form must be replaced with the MFA-capable LoginForm.");
            }
        }
        finally {
            form.reset();
        }
    };
    return (_jsxs("form", { className: className, onSubmit: (event) => void submit(event), children: [_jsxs("label", { children: ["Email", _jsx("input", { name: "email", type: "email", autoComplete: "email", required: true })] }), _jsxs("label", { children: ["Password", _jsx("input", { name: "password", type: "password", autoComplete: "current-password", required: true })] }), _jsxs("label", { children: ["Tenant code", _jsx("input", { name: "tenantCode", type: "text", autoComplete: "organization", required: true })] }), _jsxs("label", { children: [_jsx("input", { name: "remember", type: "checkbox" }), " Remember me"] }), error || mfaNotice ? _jsx("p", { role: "alert", children: error ?? mfaNotice }) : null, _jsx("button", { type: "submit", disabled: pending, children: pending ? "Signing in" : "Sign in" }), footer] }));
}
//# sourceMappingURL=client.js.map